import { supabase } from "@/lib/supabase-client";
import { UnionType, UnionUserType } from "@/types/db/unions";

export const unionService = {
  async getUnions(): Promise<(UnionType & { unions_users?: UnionUserType[] })[] | null> {
    const { data, error } = await supabase
      .schema("lists")
      .from("unions")
      .select(`id, tenant_id, name, description, meta_data, created_at`)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    // Load unions_users from link schema for each union
    const unionsWithUsers = await Promise.all(
      (data || []).map(async (union) => {
        const { data: unionsUsers } = await supabase
          .schema("link")
          .from("unions_users")
          .select("user_id, tenant_id")
          .eq("union_id", union.id);

        return {
          ...union,
          unions_users: unionsUsers || [],
        };
      }),
    );

    return unionsWithUsers as unknown as (UnionType & { unions_users?: UnionUserType[] })[];
  },

  async getUnionById(id: string): Promise<UnionType & { unions_users?: UnionUserType[] }> {
    const { data, error } = await supabase.schema("lists").from("unions").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }

    // Load unions_users from link schema
    const { data: unionsUsers } = await supabase
      .schema("link")
      .from("unions_users")
      .select("user_id, tenant_id")
      .eq("union_id", id);

    return {
      ...data,
      unions_users: unionsUsers || [],
    } as UnionType & { unions_users?: UnionUserType[] };
  },

  async addUnion(unionData: { name: string; description?: string; meta_data?: any }): Promise<UnionType | null> {
    const { data, error } = await supabase.schema("lists").from("unions").insert([unionData]).select().single();
    if (error) {
      console.error("Error adding Union:", error.message);
      throw new Error(error.message);
    }
    return data as UnionType;
  },

  async updateUnion(id: string, patch: Partial<UnionType>): Promise<UnionType> {
    const { data, error } = await supabase.schema("lists").from("unions").update(patch).eq("id", id).select().single();

    if (error) throw error;
    return data as UnionType;
  },

  async deleteUnionsByIds(unionIds: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("unions").delete().in("id", unionIds);
    if (error) {
      console.error("Error deleting Unions by IDs:", error.message);
      return false;
    }
    return true;
  },

  // Manage unions_users link table
  async addUsersToUnion(unionId: string, tenantId: string, userIds: string[]): Promise<boolean> {
    const records = userIds.map((userId) => ({
      union_id: unionId,
      user_id: userId,
      tenant_id: tenantId,
    }));

    const { error } = await supabase.schema("link").from("unions_users").insert(records);
    if (error) {
      console.error("Error adding users to union:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async removeUsersFromUnion(unionId: string, userIds: string[]): Promise<boolean> {
    const { error } = await supabase
      .schema("link")
      .from("unions_users")
      .delete()
      .eq("union_id", unionId)
      .in("user_id", userIds);

    if (error) {
      console.error("Error removing users from union:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async syncUnionUsers(unionId: string, tenantId: string, newUserIds: string[]): Promise<boolean> {
    // Get current users
    const { data: currentUsers } = await supabase
      .schema("link")
      .from("unions_users")
      .select("user_id")
      .eq("union_id", unionId);

    const currentUserIds = (currentUsers || []).map((u) => u.user_id);

    // Find users to add and remove
    const toAdd = newUserIds.filter((id) => !currentUserIds.includes(id));
    const toRemove = currentUserIds.filter((id) => !newUserIds.includes(id));

    // Remove users
    if (toRemove.length > 0) {
      await this.removeUsersFromUnion(unionId, toRemove);
    }

    // Add users
    if (toAdd.length > 0) {
      await this.addUsersToUnion(unionId, tenantId, toAdd);
    }

    return true;
  },
};
